'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Input as AriaInput } from 'react-aria-components/Input';
import { cn } from '../../utils/cn';
import { TextBaseInputWithIcons, type TextBaseInputWithIconsProps } from './TextBaseInput.tsx';
import type { InputIndication } from '../../internal/inputs/InputHelpers.tsx';
import React from 'react';

export const sharedInputVariants = cva(
    `
        w-full rounded-lr-main border-lr-main border-lr-border-active text-lr-text-main caret-lr-border-focus outline-0
        outline-offset-lr-focus outline-lr-border-focus transition-colors
        data-[disabled]:cursor-not-allowed data-[disabled]:border-lr-border-disabled data-[disabled]:bg-lr-bg-disabled
        data-[disabled]:text-lr-text-subtle data-[disabled]:placeholder:text-transparent
        data-[focus-visible]:outline-lr-focus
    `,
    {
        variants: {
            size: {
                md: 'text-lr-body-standard-light',
                sm: 'text-lr-body-balanced-light'
            },
            state: {
                default: [
                    `
                        placeholder:text-lr-text-subtle
                        placeholder-shown:border-lr-border-medium
                    `,
                    `
                        data-[hovered]:not-data-[disabled]:border-lr-border-hover
                        data-[hovered]:not-data-[disabled]:bg-lr-bg-hover
                    `
                ],
                success: 'border-lr-border-success bg-lr-bg-success-subtle text-lr-text-success',
                error: 'border-lr-border-error bg-lr-bg-error-subtle text-lr-text-error'
            }
        },
        defaultVariants: {
            size: 'md',
            state: 'default'
        }
    }
);

export const sharedInputWithIconVariants = cva('', {
    variants: {
        size: {
            sm: ['h-lr-size-500'],
            md: ['h-lr-size-600']
        },
        state: {
            default: 'group-has-data-[hovered]:border-lr-border-hover group-has-data-[hovered]:bg-lr-bg-hover',
            success: '',
            error: ''
        }
    },
    defaultVariants: {
        size: 'md',
        state: 'default'
    }
});

type BaseProps = Omit<TextBaseInputWithIconsProps, 'children' | 'notTextBasedInput'>;

export interface TextInputProps
    extends Omit<BaseProps, 'size'>,
        Omit<VariantProps<typeof sharedInputVariants>, 'state'>,
        Omit<VariantProps<typeof sharedInputWithIconVariants>, 'state'> {
    placeholder?: string;
}

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    ({ size = 'md', state = 'default', placeholder, className, indications, isRequired, ...rest }, ref) => {
        const hasError = !!indications?.some((i: InputIndication) => i.type === 'error');
        const { 'data-cerberus': dataCerberus, ...restProps } = rest as any;

        return (
            <TextBaseInputWithIcons
                state={state}
                indications={indications}
                isRequired={isRequired}
                isInvalid={hasError}
                {...(restProps as TextBaseInputWithIconsProps)}
            >
                <AriaInput
                    ref={ref}
                    className={cn(
                        sharedInputVariants({ size, state }),
                        sharedInputWithIconVariants({ size, state }),
                        className
                    )}
                    placeholder={placeholder}
                    data-cerberus={dataCerberus}
                />
            </TextBaseInputWithIcons>
        );
    }
);

TextInput.displayName = 'TwTextInput';
export default TextInput;
